import { AuditLogQueryParams } from './types';

const AUDIT_LIMIT_MIN = 1;
// The backend rejects anything above this
const AUDIT_LIMIT_MAX = 200;

type QueryValue = string | number | boolean | Date | null | undefined;

// The audit endpoint takes eight independent optional filters, so unlike the routes below
// it gets a real builder: keys are sorted to keep the output canonical, which the audit
// slice relies on to match a response to the request that is still in flight.
const withQueryString = (path: string, params: Record<string, QueryValue>): string => {
  const search = new URLSearchParams();
  Object.keys(params)
    .sort()
    .forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (value instanceof Date) {
        if (!Number.isNaN(value.getTime())) {
          search.append(key, value.toISOString());
        }
        return;
      }
      if (typeof value === 'number' && Number.isNaN(value)) {
        return;
      }
      search.append(key, String(value));
    });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
};

const clampAuditLimit = (limit?: number): number | undefined =>
  (limit === undefined
    ? undefined
    : Math.min(Math.max(Math.trunc(limit), AUDIT_LIMIT_MIN), AUDIT_LIMIT_MAX));

interface DenmProps {
  createdFrom?: string;
  updatedFrom: string;
  source: string;
  status: string;
  roadSegments?: string;
  causeId?: string;
  subCauseId?: string;
  createdTo?: string;
  updatedTo?: string;
  sincroDispatchable?: string;
  page?: number;
  limit?: number;
}

interface IvimProps {
  updatedFrom: string;
  status: string;
  source: string;
  roadSegments?: string;
  updatedTo?: string;
  page?: number;
  limit?: number;
  serviceCategoryCode?: string;
  serviceSubCategoryCode?: string;
}

const apiRoute = {
  getDENMs: ({
    createdFrom,
    updatedFrom,
    source,
    status,
    roadSegments,
    causeId,
    subCauseId,
    createdTo,
    updatedTo,
    sincroDispatchable,
    page,
    limit,
  }: DenmProps) =>
    `api/etsi/v1alpha1/denms?${roadSegments}&${status}&updatedAt=${updatedFrom}&${source}${causeId}${subCauseId}${updatedTo}${sincroDispatchable}${
      page ? `&page=${page}` : ''
    }${limit ? `&limit=${limit}` : ''}`,
  getDENM: (id: string) => `api/etsi/v1alpha1/denms/${id}`,
  createDENM: `api/etsi/v1alpha1/denms`,
  updateDENM: (id: string) => `api/etsi/v1alpha1/denms/${id}`,
  deleteDENM: (id: string) => `api/etsi/v1alpha1/denms/${id}`,
  deleteDENMs: `api/etsi/v1alpha1/denms/set_deleted`,
  disableDENM: (disable: string) => `api/etsi/v1alpha1/denms/set_disabled/${disable}`,
  getIVIMs: ({
    updatedFrom,
    status,
    source,
    roadSegments,
    updatedTo,
    serviceCategoryCode,
    serviceSubCategoryCode,
    page,
    limit,
  }: IvimProps) =>
    `api/etsi/v1alpha1/ivims?${roadSegments}&${status}&updatedAt=${updatedFrom}&${updatedTo}&${source}&${serviceCategoryCode}&${serviceSubCategoryCode}${
      page ? `&page=${page}` : ''
    }${limit ? `&limit=${limit}` : ''}`,
  createIVIM: `api/etsi/v1alpha1/ivims`,
  updateIVIM: (id: string) => `api/etsi/v1alpha1/ivims/${id}`,
  deleteIVIM: (id: string) => `api/etsi/v1alpha1/ivims/${id}`,
  deleteIVIMs: `api/etsi/v1alpha1/ivims/set_deleted`,
  disableIVIM: (disable: string) => `api/etsi/v1alpha1/ivims/set_disabled/${disable}`,
  devices: (roadSegments: string, active?: boolean) =>
    `api/devices/v1alpha1/rsus?${roadSegments}${active !== undefined ? `&alive=${active}` : ''}`,
  device: (id: string) => `api/devices/v1alpha1/rsus/${id}`,
  createRSU: `api/devices/v1alpha1/rsus`,
  updateRSU: (id: string) => `api/devices/v1alpha1/rsus/${id}`,
  deleteRSU: (id: string) => `api/devices/v1alpha1/rsus/${id}`,
  getVms: `api/etsi/v1alpha1/vms`,
  updateVms: (id: string) => `api/etsi/v1alpha1/vms/${id}`,
  deleteVms: (id: string) => `api/etsi/v1alpha1/vms/${id}`,
  getVmsIvim: (id: string) => `api/etsi/v1alpha1/vms/${id}/ivim`,
  roads: `api/roads/v1alpha1/segments`,
  causes: `api/etsi/v1alpha1/denm-causes`,
  subCauses: `api/etsi/v1alpha1/denm-subcauses?limit=200`,
  subCausesByCause: (causeId: string) => `api/etsi/v1alpha1/denm-causes/${causeId}/subcauses`,
  getSettings: `settings`,
  updateSettings: (path: string, setting: string) => `settings/${path}/${setting}`,
  getCams: `api/etsi/v1alpha1/cams`,
  /** CAM grid: vehicle count per cell. start/end ISO 8601. */
  camsGridVehicleCounts: (start: string, end: string) =>
    `api/etsi/v1alpha1/cams/grid-vehicle-counts?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  /** CAM grid: average speed per cell. start/end ISO 8601. */
  camsGridSpeedValues: (start: string, end: string) =>
    `api/etsi/v1alpha1/cams/grid-speed-values?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
  getRsuDowntimes: (start: string, end: string) =>
    `api/devices/v1alpha1/rsus/downtimes/segments?start=${start}&end=${end}`,
  spatems: (roadSegments: string) =>
    `api/etsi/v1alpha1/spatems?${roadSegments}`,
  getVersion: 'version',
  auditLogs: (params: AuditLogQueryParams = {}): string =>
    withQueryString('api/audit/v1alpha1/audit-logs', {
      ...params,
      limit: clampAuditLimit(params.limit),
    }),
};

export default apiRoute;
